/* eslint-disable react/prop-types */
import React, { useEffect, useRef, useState, useMemo } from "react";
import clsx from "clsx";
import PropTypes from "prop-types";
import { InfiniteLoader, List, WindowScroller } from "react-virtualized";
import AutoSizer from "react-virtualized/dist/commonjs/AutoSizer";
import { Popover, LinearProgress } from "@material-ui/core";
import { AuthChecker, Button } from "../../atomicComponents";
import styles from "./Table.module.css";
import { columnsPropType } from "./types";
import { TableHead, SelectionMenu, TableRow } from "./components";
import { Link, useFetcher } from "react-router-dom";
import DataNotFound from "../DataNotFound";
import { resync_fuel_count } from "../../constants/constVariables";
import { styled } from '@mui/material/styles';
import Typography from '@mui/material/Typography';
import Tooltip, { TooltipProps, tooltipClasses } from '@mui/material/Tooltip';

function filterBySearchTexts(fieldToSearchTextMap, data, columns) {
  const fieldToSearchFnMap = columns.reduce((acc, col) => {
    if (col.searchFn) return { ...acc, [col.field]: col.searchFn };
    return acc;
  }, {});

  let filteredData = [...data];
  Object.entries(fieldToSearchTextMap).forEach((entry) => {
    const [field, searchText] = entry;
    const defaultSearchFn = (fieldValue, searchValue) =>
      fieldValue.toLowerCase().startsWith(searchValue);

    const searchFn = fieldToSearchFnMap[field] || defaultSearchFn;

    filteredData = filteredData.filter(
      (row) => !searchText || !row[field] || searchFn(row[field], searchText)
    );
  });
  return filteredData;
}

function filterBySelectedFilters(fieldToFiltersSetMap, data, columns) {
    const fieldToFilterFnMap = columns.reduce((acc, col) => {
    if (col.filterFn) return { ...acc, [col.field]: col.filterFn };
    return acc;
  }, {});

  let filteredData = [...data];
  Object.entries(fieldToFiltersSetMap).forEach((entry, idx) => {
    const [field, filtersSet] = entry;
    const filterFn = fieldToFilterFnMap[field];

    filteredData = filteredData.filter((row) => {
      if (!filterFn) {
        return (
          !filtersSet?.size ||
          filtersSet.has(row[field]) ||
          (row[field] instanceof Array &&
            row[field].some((i) => [...filtersSet].includes(i)))
        );
      }
      if (!idx) {
        console.log(" [...filtersSet]", [...filtersSet]);
      }

      return (
        !filtersSet?.size ||
        [...filtersSet].some((filter) =>
          filterFn(row, filter.value ? filter.value : filter)
        )
      );
    });
  });
  return filteredData;
}

function deepCompareEqualsColumnsArray(oldArray, newArray) {
  if (oldArray?.length !== newArray?.length) return false;
  const zippedArray = oldArray.map((e, i) => [e, newArray[i]]);
  return zippedArray.every((tuple) => tuple[0].title === tuple[1].title);
}

function useDeepCompareColumnArraysMemoize(value) {
  const ref = useRef();
  // it can be done by using useMemo as well
  // but useRef is rather cleaner and easier

  if (!deepCompareEqualsColumnsArray(value, ref.current)) {
    ref.current = value;
  }

  return ref.current;
}

function useDeepCompareColumnArraysEffect(callback, dependencies) {
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(callback, dependencies.map(useDeepCompareColumnArraysMemoize));
}

const Table = ({
  rows: data,
  columns: originalColumns,
  classNames = {},
  rowHeight = 38,
  headerHeight,
  defaultColumns: originalDefaultColumns,
  removeRowSelection,
  primaryAction,
  secondaryActions = [],
  setHasActiveEdits,
  setAsnCount,
  isDataLoading,
  hideActionButtons,
  showRadioButton,
  onSelect,
  tableName,
  pagination,
  infiniteScroll,
  onRowSelect,
  loadMoreData,
  get,
  openUploadFile,
  showBulkSearchMain,
  showBulkSearch,
  hasNext,
  onClickPath,
  handleSingleCheckbox,
  handleCheckbox,
  selectCount,
  checkedState,
  setTotalData,
  clearBulkSearch,
  selectedRows,
  updateSetSelectedRows,
  bulkSearchButton,
  tableFilter,
  setTableFilter
}) => {
  const [rowsData, setRowsData] = useState({
    data: [...data],
    filteredData: [...data],
  });

  const [fieldToSearchTextMap, setFieldToSearchTextMap] = useState({});
  const [isEllipsisPopupOpen, setIsEllipsisPopupOpen] = useState(false);
  const [ellipsisAnchorEl, setEllipsisAnchorEl] = useState(null);
  const [ellipsisPopupValue, setEllipsisPopupValue] = useState(null);
  const [fieldToFiltersSetMap, setFieldToFiltersSetMap] = useState(tableFilter?tableFilter:{});
  const butonRef = useRef(0);

  const closeEllipsisPopup = () => setIsEllipsisPopupOpen(false);

  const vListRef = useRef(null);
  const tableHeadRef = useRef(null);

  const handleListScroll = (e) => {
    tableHeadRef.current.scrollLeft = e.target.scrollLeft;
  };

  const syncListToTableHead = (e) => {
    vListRef.current.scrollLeft = e.target.scrollLeft;
  };

  useEffect(() => {
    // eslint-disable-next-line prefer-destructuring
    vListRef.current = document.getElementsByClassName(
      "ReactVirtualized__Grid ReactVirtualized__List"
    )[0];

    if (vListRef.current && tableHeadRef) {
      vListRef.current.addEventListener("scroll", handleListScroll);
      tableHeadRef.current.addEventListener("scroll", syncListToTableHead);
    }
  }, []);

  useEffect(() => {
    // TODO: Fix this
    if (setAsnCount) {
      setAsnCount(rowsData.filteredData.length);
    }
  }, [setAsnCount, rowsData]);

  const handlePopoverOpen = (event, val) => {
    if (event.currentTarget.scrollWidth > event.currentTarget.clientWidth) {
      setEllipsisPopupValue(val);
      setIsEllipsisPopupOpen(true);
      setEllipsisAnchorEl(event.currentTarget);
    }
  };

  const handlePopoverClose = () => {
    setIsEllipsisPopupOpen(false);
    setEllipsisAnchorEl(null);
  };

  const toggleSelectAllRows = () => {
    var ab = []
    // console.log("ab in table = ", rowsData)
    setRowsData((prev) => {
      const visibleRowIdsSet = new Set(prev.filteredData.map((row) => row.id));
      const isSelected = !prev.filteredData.every((row) => row.isSelected);
      // const visibleRowIdsSet2 = new Set(prev.filteredData.map((row) => row.id));
      ab.push(...visibleRowIdsSet)
      // console.log("data inside = ", visibleRowIdsSet, isSelected)
      return {
        ...prev,
        filteredData: prev.filteredData.map((row) => ({ ...row, isSelected })),
        data: prev.data.map((row) => {
          if (visibleRowIdsSet.has(row.id)) return { ...row, isSelected };
          // ab.push(row)
          return row;
        }),
      };
    });
    // console.log("ab in table = ", ab, rowsData)
    updateSetSelectedRows("all", rowsData.filteredData, ab);
    handleCheckbox();
  };
  // useEffect(() => { console.log("rowsData inside Table = ", rowsData) }, [rowsData])
  const handleRowSelection = (selectedId) => {
    // console.log("selected Id = ", selectedId, rowsData)
    setRowsData((prev) => ({
      ...prev,
      data: prev.data.map((row) =>
        row.id === selectedId + 1 ? { ...row, isSelected: !row.isSelected } : row
      ),
    }));
    // console.log("path = ", selectedRows, selectedRows[selectedId], selectedId)
    if (updateSetSelectedRows) updateSetSelectedRows(selectedId);
    handleSingleCheckbox(selectedId);
  };

  const handleRadioRowSelection = (item) => {
    setRowsData((prev) => ({
      ...prev,
      data: prev.data.map((row) =>
        row.id === item.id
          ? { ...row, isSelected: true }
          : { ...row, isSelected: false }
      ),
    }));
    onSelect(item);
  };

  useEffect(() => {
    if (setHasActiveEdits) {
      const areEditsActive = rowsData.data.some((row) => row.isSelected);
      setHasActiveEdits(areEditsActive);
    }
  }, [rowsData.data, setHasActiveEdits]);

  useEffect(() => {
    setTotalData(rowsData?.filteredData?.length);
  }, [rowsData]);

  useEffect(() => {
    if(setTableFilter){
      setTableFilter(fieldToFiltersSetMap);
    }
  },[fieldToFiltersSetMap])
  
  const calcWidth = (value) => {
    var canvas = document.createElement("canvas");
    var ctx = canvas.getContext("2d");
    ctx.font = "15px sans-serif";  // This can be set programmaticly from the element's font-style if desired
    var textWidth = ctx.measureText(value).width;
    // console.log("elele = ", Math.ceil(textWidth));
  }
  calcWidth(String(rowsData.data.length).length)
  const defaultColumns =
    // useMemo(
    // () =>
    [
      showRadioButton
        ? null
        : {
          title: "#",
          field: "_serialNumber",
          // width: calcWidth(String(rowsData.data.length).length),
          width: String(rowsData.data.length).length > 1 ? (String(rowsData.data.length).length * 10) + 5 : 22,
          render: (_, idx) => idx,
          headerStyles: {
            paddingLeft: 7,
            paddingRight: 2,
          },
        },
      (primaryAction || secondaryActions?.length) && !removeRowSelection
        ? {
          title: "select",
          field: "_select",
          width: 30,
          renderHeader: (rows) => (
            (
              <input
                type="checkbox"
                onChange={toggleSelectAllRows}
                checked={
                  rows?.length &&
                  selectedRows?.length &&
                  rowsData.filteredData.every((row) => row.isSelected)
                }
              //   disabled={selectedColumns?.length == 2}
              />
            )
          ),
          render: ({ rows, id }) => (
            // console.log("rows rows", rowsData.data.length, String(rowsData.data.length).length),
            // console.log("rows id", id),
            <input
              style={{ marginTop: 0, marginBottom: 0 }}
              type="checkbox"
              onChange={() => handleRowSelection(id - 1)}
              checked={selectedRows ? selectedRows[id - 1] : false}
              data-testid={`select-row-${id}`}
              id={`select-row-${id}`}
              name={`select-row-${id}`}
            // disabled={selectedColumns?.length == 2}
            />
          ),
        }
        : null,

      ...originalDefaultColumns,
    ].filter((i) => i);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  //     [originalDefaultColumns],
  // );

  const radioColumn =
    // useMemo(
    //     () =>
    [
      showRadioButton
        ? {
          title: "select",
          field: "_select",
          width: 30,
          renderHeader: (rows) => (
            <input
              type="radio"
              onChange={toggleSelectAllRows}
              // eslint-disable-next-line react/destructuring-assignment
              checked={
                rows?.length &&
                selectedRows?.length &&
                selectedRows.every((row) => row)
              }
            />
          ),
          render: (row) => (
            <input
              style={{ marginTop: 0, marginBottom: 0 }}
              type="radio"
              onChange={() => handleRadioRowSelection(row)}
              checked={selectedRows ? selectedRows[row.id] : false}
              data-testid={`radio-row-${row.id}`}
              id={`radio-row-${row.id}`}
              name={`radio-row-${tableName}`}
            />
          ),
        }
        : null,
    ];
  // )
  const [selectedColumns, setSelectedColumns] = useState(defaultColumns);

  const noOfFixedColumns = primaryAction || secondaryActions?.length ? 2 : 1;

  const [isCustomizeTableMenuOpen, setIsCustomizeTableMenuOpen] =
    useState(false);
  const [showCopyAlert, setShowCopyAlert] = useState(false);

  const customizeTableButtonRef = useRef(null);

  const reorder = (list, startIndex, endIndex) => {
    const result = [...list];
    const [removed] = result.splice(startIndex, 1);
    result.splice(endIndex, 0, removed);

    return result;
  };

  const reorderHeaders = (result) => {
    // dropped outside the list or dropped at serial number place
    if (!result.destination || result.destination.index < noOfFixedColumns) {
      return;
    }

    const reorderedColumns = reorder(
      selectedColumns,
      result.source.index,
      result.destination.index
    );

    setSelectedColumns(reorderedColumns);
  };

  const rowRenderer = ({ style, index, key }) => {
    const handleCopy = (text) => {
      navigator.clipboard.writeText(text);
      setShowCopyAlert(true);
    };
    // console.log("Data",data);
    return (
      <TableRow
        index={index}
        style={style}
        key={key}
        row={rowsData.filteredData[index]}
        columns={
          showRadioButton
            ? [...radioColumn, ...selectedColumns]
            : [...selectedColumns]
        }
        classNames={{ ...classNames }}
        rowHeight={rowHeight}
        onCopy={handleCopy}
        handlePopoverOpen={handlePopoverOpen}
        handlePopoverClose={handlePopoverClose}
        onClickPath={onClickPath}
      // rowClick={(e) => onRowSelect(e)}
      />
    );
  };
  // console.log("selectedColumns", selectedColumns);

  const handleColumnSelectionChange = (columnTitle) => {
    if (selectedColumns.map(({ title }) => title).includes(columnTitle))
      return setSelectedColumns((prev) =>
        prev.filter((column) => column.title !== columnTitle)
      );
    return setSelectedColumns((prev) => [
      ...prev,
      ...originalColumns.filter(({ title }) => title === columnTitle),
    ]);
  };

  const applyColumnsSelections = (columnSelections) => {
    // console.log(
    //   "columnSelections",
    //   columnSelections,
    //   defaultColumns,
    //   originalDefaultColumns,
    //   originalColumns
    // );
    setSelectedColumns([
      ...defaultColumns.slice(
        0,
        defaultColumns.length - originalDefaultColumns.length
      ),
      ...originalColumns.filter((col) => columnSelections.includes(col.title)),
    ]);
  };

  useEffect(() => {
    if (showCopyAlert) setShowCopyAlert(false);
  }, [showCopyAlert]);

  //  taken from https://stackoverflow.com/questions/54095994/react-useeffect-comparing-objects
  useDeepCompareColumnArraysEffect(() => {
    setSelectedColumns(defaultColumns);
  }, [defaultColumns]);

  useEffect(() => {
    // console.log("DADadASDAD",selectedRows,defaultColumns)
    setSelectedColumns(defaultColumns);
  }, [selectedRows]);

  useEffect(() => {
        setRowsData((prevState) => {
      const dataFilteredBySearchTexts = filterBySearchTexts(
        fieldToSearchTextMap,
        prevState.data,
        selectedColumns
      );

      const dataFilteredBySearchAndFilters = filterBySelectedFilters(
        fieldToFiltersSetMap,
        dataFilteredBySearchTexts,
        selectedColumns
      );
        
      return { ...prevState, filteredData: dataFilteredBySearchAndFilters };
    });
      }, [
    selectedColumns,
    fieldToFiltersSetMap,
    fieldToSearchTextMap,
    rowsData.data,
  ]);
  
  useEffect(() => {
        setRowsData({
      data: [...data],
      filteredData: [...data],
    });
  }, [data]);

  const isRowLoaded = ({ index }) => !!rowsData.data[index];

  const loadMoreRows = () =>
    new Promise((resolve) => {
      if (!pagination.last) {
        pagination.setCurrentPageNumber((prev) => ({
          ...prev,
          number: prev.number + 1,
        }));
        resolve();
      } else {
        resolve();
      }
    });
  const HtmlTooltip = styled(({ className, ...props }) => (
    <Tooltip {...props} classes={{ popper: className }} />
  ))(({ theme }) => ({
    [`& .${tooltipClasses.tooltip}`]: {
      backgroundColor: '#fff',
      color: '#707070',
      maxWidth: 300,
      fontSize: theme.typography.pxToRem(12),
      border: '1px solid #dadde9',
      boxShadow: '0px 3px 6px rgba(0, 0, 41, 0.1);',
      FontFace: ' "Open Sans", sans-serif;',
      fontWeight: '700',
    },
  }));
  return (
    <>
      <div
        style={{ width: "100%", height: "100%" }}
        className={clsx(
          styles.baseContainer,
          classNames.tableContainerClassName
        )}
      >
        {rowsData.data.length > 0 ? (
          <div
            style={{ width: "100%" }}
            className={clsx(
              styles.basicTableContainer,
              classNames.tableContainerClassName
            )}
          >
            <>
              {/* <div className='dispatch-table-heading regularFont'> {selectCount} Selected </div> */}
              {infiniteScroll ? (
                <WindowScroller>
                  {({ height, isScrolling, onChildScroll, scrollTop }) => (
                    <InfiniteLoader
                      isRowLoaded={isRowLoaded}
                      loadMoreRows={loadMoreData}
                      rowCount={rowsData.data.length + 1}
                      threshold={1}
                    >
                      {({ onRowsRendered, registerChild }) => (
                        <AutoSizer>
                          {({ width: windowWidth, height: windowHeight }) => {
                            const width = windowWidth;
                            const height = windowHeight;
                            const listHeight = height - rowHeight - 25;
                            return (
                              <div
                                className={clsx(
                                  styles.basicTable,
                                  classNames.tableClassName
                                )}
                                style={{
                                  height,
                                  width,
                                }}
                              >
                                <div
                                  style={
                                    showRadioButton ? { marginLeft: 38 } : null
                                  }
                                >
                                  <TableHead
                                    columns={selectedColumns}
                                    ref={tableHeadRef}
                                    className={classNames.tableHeadClassName}
                                    height={headerHeight || rowHeight}
                                    reorderHeaders={reorderHeaders}
                                    rows={rowsData.filteredData}
                                    allRows={rowsData.data}
                                    fieldToSearchTextMap={fieldToSearchTextMap}
                                    setSearchTextForTitleCurry={(field) =>
                                      (value) =>
                                        setFieldToSearchTextMap((prev) => ({
                                          ...prev,
                                          [field]: value.toLowerCase(),
                                        }))}
                                    noOfFixedColumns={noOfFixedColumns}
                                    fieldToFiltersSetMap={fieldToFiltersSetMap}
                                    setFiltersForFieldCurry={(field) =>
                                      (selectedFilters) =>
                                        setFieldToFiltersSetMap((prev) => ({
                                          ...prev,
                                          [field]: new Set(selectedFilters),
                                        }))}
                                  />
                                </div>
                                <List
                                  ref={registerChild}
                                  containerStyle={{ overflow: "initial" }}
                                  rowRenderer={rowRenderer}
                                  height={listHeight}
                                  rowHeight={rowHeight}
                                  rowCount={rowsData.filteredData.length}
                                  width={width}
                                  className={clsx(
                                    styles.scrollBar,
                                    styles.heightAdjustedListScroll
                                  )}
                                  overscanRowCount={10}
                                  onRowsRendered={onRowsRendered}
                                />
                                {isDataLoading && (
                                  <div>
                                    <LinearProgress />
                                  </div>
                                )}
                              </div>
                            );
                          }}
                        </AutoSizer>
                      )}
                    </InfiniteLoader>
                  )}
                </WindowScroller>
              ) : (
                <AutoSizer>
                  {({ width: windowWidth, height: windowHeight }) => {
                    const width = windowWidth;
                    const height = windowHeight;
                    const listHeight = height - rowHeight - 2;
                    return (
                      <div
                        className={clsx(
                          styles.basicTable,
                          classNames.tableClassName
                        )}
                        style={{
                          height,
                          width,
                        }}
                      >
                        <div
                          style={showRadioButton ? { marginLeft: 38 } : null}
                        >
                          <TableHead
                            columns={selectedColumns}
                            ref={tableHeadRef}
                            className={classNames.tableHeadClassName}
                            height={headerHeight || rowHeight}
                            reorderHeaders={reorderHeaders}
                            rows={rowsData.filteredData}
                            allRows={rowsData.data}
                            fieldToSearchTextMap={fieldToSearchTextMap}
                            setSearchTextForTitleCurry={(field) => (value) =>
                              setFieldToSearchTextMap((prev) => ({
                                ...prev,
                                [field]: value.toLowerCase(),
                              }))}
                            noOfFixedColumns={noOfFixedColumns}
                            fieldToFiltersSetMap={fieldToFiltersSetMap}
                            setFiltersForFieldCurry={(field) =>
                              (selectedFilters) =>
                                setFieldToFiltersSetMap((prev) => ({
                                  ...prev,
                                  [field]: new Set(selectedFilters),
                                }))}
                          />
                        </div>
                        <List
                          ref={vListRef}
                          containerStyle={{ overflow: "initial" }}
                          rowRenderer={rowRenderer}
                          height={listHeight}
                          rowHeight={rowHeight}
                          rowCount={rowsData.filteredData.length}
                          width={width}
                          className={clsx(
                            styles.scrollBar,
                            styles.heightAdjustedListScroll
                          )}
                          overscanRowCount={10}
                        />
                        {isDataLoading && (
                          <div>
                            <LinearProgress />
                          </div>
                        )}
                      </div>
                    );
                  }}
                </AutoSizer>
              )}
            </>
          </div>
        ) : (
          <DataNotFound />
        )}


        <div className={` TableButton_inner ${styles.actionsBar}`}>

          {rowsData.data.length > 0 ?
            <div style={{ display: "flex", gap: "1rem" }}>
              <Button
                className={clsx(
                  styles.actionButton,
                  styles.cutomizeTableButton,
                  "smallFont"
                )}
                variant="tertiary"
                // style={{backgroundColor:"#FFA222",color:"#FFF",marginRight:"1rem"}}
                onClick={() => setIsCustomizeTableMenuOpen(true)}
                ref={customizeTableButtonRef}
              >
                CUSTOMIZE TABLE
              </Button>

              {secondaryActions.map((action, index) => action? (
                <AuthChecker operation={action.authOperation} key={index}>
                  {(isAuthorized) => (
                    action.toolTipHover ?
                      <HtmlTooltip
                        enterDelay={200} leaveDelay={100}
                        TransitionProps={{ timeout: 100 }}
                        title={
                          <React.Fragment>
                            {action.toolTipMessage}
                          </React.Fragment>
                        }
                      >
                        <span><Button
                          ref={butonRef}
                          variant="primary"
                          className={clsx(
                            styles.actionButton,
                            styles.cutomizeTableButton
                          )}
                          style={{
                            backgroundColor: action.backgroundColor,
                          }}
                          simpleClassName={action.name}
                          onClick={(e) =>
                            action.actionFn(
                              rowsData.data.filter((row) => row.isSelected),
                              e
                            )
                          }
                          disabled={
                            (!isAuthorized) ||
                            ((!action.shouldEnable && selectCount > 1 && action.name == "Edit") ||
                              (!action.shouldEnable && !(selectedRows.filter(Boolean).length > 0 && selectedRows.filter(Boolean).length < resync_fuel_count) && action.name == "Rsync Fuel") ||
                              (!selectedRows ? true : selectedRows.every((v) => v === false)))
                          }
                        // disabled={ action.authOperation && (!isAuthorized || !action.shouldEnable) }
                        >
                          {action.name}
                        </Button></span>
                      </HtmlTooltip> :
                      <Button
                        ref={butonRef}
                        variant="primary"
                        className={clsx(
                          styles.actionButton,
                          styles.cutomizeTableButton
                        )}
                        style={{
                          backgroundColor: action.backgroundColor,
                        }}
                        simpleClassName={action.name}
                        onClick={(e) =>
                          action.actionFn(
                            rowsData.data.filter((row) => row.isSelected),
                            e
                          )
                        }
                        disabled={
                          (!isAuthorized) ||
                          ((!action.shouldEnable && selectCount > 1 && action.name == "Edit") ||
                            (!action.shouldEnable && !(selectedRows.filter(Boolean).length > 0 && selectedRows.filter(Boolean).length < resync_fuel_count) && action.name == "Rsync Fuel") ||
                            (!selectedRows ? true : selectedRows.every((v) => v === false)))
                        }
                      // disabled={ action.authOperation && (!isAuthorized || !action.shouldEnable) }
                      >
                        {action.name}
                      </Button>
                  )}
                </AuthChecker>
              ):null)}
              {
                showBulkSearch === true ?
                  <>
                    {bulkSearchButton === false ?
                      <Button
                        variant="primary"
                        style={{
                          backgroundColor: "#82C1A7",
                        }}
                        onClick={showBulkSearchMain}
                      >
                        BULK SEARCH
                      </Button> : null}
                    {bulkSearchButton === true ?
                      <Button
                        id="clear_bulk_search"
                        className={clsx(
                          styles.actionButton,
                          styles.clearFiltersButton
                        )}
                        variant="secondary"
                        onClick={clearBulkSearch}
                      >
                        CLEAR SEARCH
                      </Button> : null
                    }

                  </>
                  :
                  null
              }
              <Button
                className={clsx(
                  styles.actionButton,
                  styles.clearFiltersButton
                )}
                variant="secondary"
                onClick={() => setFieldToFiltersSetMap({})}
              >
                Clear Filters
              </Button>
            </div>
            : null}
          <div
            style={
              rowsData.data.length > 0
                ? null
                : { position: "absolute", right: "0", bottom: "1rem" }
            }
          >
            {primaryAction && (
              <AuthChecker operation={primaryAction.authOperation}>
                {(isAuthorized) => (
                  <Button
                    className={clsx(
                      styles.actionButton,
                      styles.primaryActionButton,
                      "smallFont"
                    )}
                    style={{
                      backgroundColor: primaryAction.backgroundColor,
                    }}
                    variant="primary"
                    onClick={() => primaryAction.actionFn()}
                    disabled={!isAuthorized || !primaryAction.shouldEnable}
                    data-testid="primary-action"
                    simpleClassName={primaryAction.name}
                  >
                    {primaryAction.name}
                  </Button>
                )}
              </AuthChecker>
            )}
          </div>
        </div>

      </div>
      <SelectionMenu
        anchorEl={customizeTableButtonRef.current}
        open={isCustomizeTableMenuOpen}
        handleClose={() => setIsCustomizeTableMenuOpen(false)}
        options={originalColumns.map(({ title }) => title)}
        currentSelections={selectedColumns
          .filter(({ title }) => title !== "select" && title !== "#")
          .map(({ title }) => title)}
        handleChange={handleColumnSelectionChange}
        resetToDefault={() => setSelectedColumns([...defaultColumns])}
        applySelections={applyColumnsSelections}
      />
      <Popover
        open={isEllipsisPopupOpen}
        anchorEl={ellipsisAnchorEl}
        onClose={closeEllipsisPopup}
        className={styles.popover}
        classes={{
          paper: clsx(styles.popoverPaper, "smallFont"),
        }}
        anchorOrigin={{
          vertical: "bottom",
          horizontal: "left",
        }}
        transformOrigin={{
          vertical: "top",
          horizontal: "left",
        }}
      >
        {ellipsisPopupValue}
      </Popover>
      <div
        className={clsx(
          styles.copyAlert,
          showCopyAlert ? styles.visible : styles.hidden
        )}
      >
        Text copied
      </div>
    </>
  );
};

Table.propTypes = {
  classNames: PropTypes.shape({
    tableContainerClassName: PropTypes.string,
    tableClassName: PropTypes.string,
    tableRowClassName: PropTypes.oneOfType([PropTypes.string, PropTypes.func]),
    tableHeadClassName: PropTypes.string,
    tableCellClassName: PropTypes.string,
  }),
  rows: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.number.isRequired,
    })
  ),
  columns: columnsPropType,
  rowHeight: PropTypes.number,
  headerHeight: PropTypes.number,
  defaultColumns: columnsPropType,
  removeRowSelection: PropTypes.bool,
  primaryAction: PropTypes.shape({
    name: PropTypes.string.isRequired,
    actionFn: PropTypes.func.isRequired,
    path: PropTypes.string,
  }),
  secondaryActions: PropTypes.arrayOf(
    PropTypes.shape({
      name: PropTypes.string.isRequired,
      actionFn: PropTypes.func.isRequired,
      backgroundColor: PropTypes.string,
    })
  ),
  setHasActiveEdits: PropTypes.func,
  setAsnCount: PropTypes.func,
  isDataLoading: PropTypes.bool,
  hideActionButtons: PropTypes.bool,
  showRadioButton: PropTypes.bool,
  onSelect: PropTypes.func,
  tableName: PropTypes.string,
  pagination: PropTypes.any,
  infiniteScroll: PropTypes.any,
  onRowSelect: PropTypes.func,
  hasNext: PropTypes.bool,
  loadMoreData: PropTypes.func,
  get: PropTypes.func,
  handleSingleCheckbox: PropTypes.func,
  handleCheckbox: PropTypes.func,
  onClickPath: PropTypes.string.isRequired,
  selectCount: PropTypes.number,
  setTotalData: PropTypes.func,
  updateSetSelectedRows: PropTypes.func,
  openUploadFile: PropTypes.func,
  showBulkSearch: PropTypes.bool,
  showBulkSearchMain: PropTypes.func,
  clearBulkSearch: PropTypes.func,
  bulkSearchButton: PropTypes.bool,
  setTableFilter:PropTypes.func,
  tableFilter:PropTypes.object
};

export default Table;
